/**
 * Workspace Store
 *
 * Manages workspace state including active workspace selection.
 * Persists active workspace ID to localStorage.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isDefault: boolean;
  role: WorkspaceRole;
  isOwner: boolean;
  isActive: boolean;
  createdAt: string;
}

interface WorkspaceState {
  // Active workspace ID (persisted)
  activeWorkspaceId: string | null;

  // Cached workspace list (not persisted, fetched from API)
  workspaces: Workspace[];

  // Loading state
  isLoading: boolean;

  // Actions
  setActiveWorkspace: (workspaceId: string) => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  setLoading: (loading: boolean) => void;
  clearWorkspaces: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      activeWorkspaceId: null,
      workspaces: [],
      isLoading: false,

      setActiveWorkspace: (workspaceId: string) => {
        set({ activeWorkspaceId: workspaceId });
      },

      setWorkspaces: (workspaces: Workspace[]) => {
        const state = get();
        // If no active workspace set, use the first one or the one marked active from server
        let activeId = state.activeWorkspaceId;
        if (!activeId && workspaces.length > 0) {
          const activeWorkspace = workspaces.find((ws) => ws.isActive);
          const firstWorkspace = workspaces[0];
          activeId = activeWorkspace?.id ?? firstWorkspace?.id ?? null;
        }
        set({ workspaces, activeWorkspaceId: activeId });
      },

      setLoading: (isLoading: boolean) => set({ isLoading }),

      clearWorkspaces: () => set({ workspaces: [], activeWorkspaceId: null }),
    }),
    {
      name: 'arml-workspace-store',
      partialize: (state) => ({ activeWorkspaceId: state.activeWorkspaceId }),
    }
  )
);

/**
 * Get the active workspace from the store
 */
export function getActiveWorkspace(state: WorkspaceState): Workspace | null {
  if (!state.activeWorkspaceId) return null;
  return state.workspaces.find((ws) => ws.id === state.activeWorkspaceId) ?? null;
}

/**
 * Check if user can perform an action based on their role in the active workspace
 */
export function canPerformAction(
  role: WorkspaceRole,
  action: 'view' | 'create' | 'edit' | 'delete' | 'invite' | 'manage_roles'
): boolean {
  const roleLevel: Record<WorkspaceRole, number> = {
    viewer: 0,
    member: 1,
    admin: 2,
    owner: 3,
  };

  const requiredLevel: Record<typeof action, number> = {
    view: 0,
    create: 1,
    edit: 1,
    delete: 2,
    invite: 2,
    manage_roles: 3,
  };

  return roleLevel[role] >= requiredLevel[action];
}
